import { useMemo, useState } from "react";
import { isProductionEnvironment } from "./environmentConfig";

const MODE = {
  LOGIN: "login",
  REGISTER: "register"
};

const REGISTER_STEP = {
  FRIEND_ID: "friend-id",
  PASSWORD: "password"
};

export default function LoginPage({
  activeEnvironment,
  showEnvironmentUi,
  onLogin,
  onRegisterCheck,
  onRegister,
  busy,
  error,
  registerCheckBusy,
  registerCheckError,
  registerBusy,
  registerError
}) {
  const showEnvironment =
    showEnvironmentUi && Boolean(activeEnvironment?.label) && !isProductionEnvironment(activeEnvironment);
  const [mode, setMode] = useState(MODE.LOGIN);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [friendlyId, setFriendlyId] = useState("");
  const [registerStep, setRegisterStep] = useState(REGISTER_STEP.FRIEND_ID);
  const [verifiedFriendlyId, setVerifiedFriendlyId] = useState("");
  const [verifiedDisplayName, setVerifiedDisplayName] = useState("");
  const [verifiedLevel, setVerifiedLevel] = useState(0);
  const [registerPassword, setRegisterPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [clientRegisterError, setClientRegisterError] = useState("");

  const activeError = useMemo(() => {
    if (mode === MODE.REGISTER) {
      const stepError =
        registerStep === REGISTER_STEP.FRIEND_ID ? registerCheckError : registerError;
      return clientRegisterError || stepError;
    }
    return error;
  }, [clientRegisterError, error, mode, registerCheckError, registerError, registerStep]);

  function switchMode(nextMode) {
    setMode(nextMode);
    setClientRegisterError("");
    setRegisterStep(REGISTER_STEP.FRIEND_ID);
    setFriendlyId("");
    setVerifiedFriendlyId("");
    setVerifiedDisplayName("");
    setVerifiedLevel(0);
    setRegisterPassword("");
    setConfirmPassword("");
  }

  function submitLogin() {
    onLogin(username, password);
  }

  async function submitRegisterCheck() {
    setClientRegisterError("");
    if (!friendlyId.trim()) {
      setClientRegisterError("Friend ID is required.");
      return;
    }

    try {
      const payload = await onRegisterCheck(friendlyId.trim());
      setVerifiedFriendlyId(payload.friendly_id);
      setVerifiedDisplayName(payload.display_name);
      setVerifiedLevel(payload.level);
      setRegisterStep(REGISTER_STEP.PASSWORD);
      setRegisterPassword("");
      setConfirmPassword("");
    } catch {
      // RootApp owns the server error state.
    }
  }

  function submitRegister() {
    setClientRegisterError("");

    if (!verifiedFriendlyId) {
      setClientRegisterError("Verify the Friend ID before creating the account.");
      return;
    }
    if (registerPassword !== confirmPassword) {
      setClientRegisterError("Passwords do not match.");
      return;
    }
    if (registerPassword.length < 6) {
      setClientRegisterError("Password must have at least 6 characters.");
      return;
    }

    onRegister(verifiedFriendlyId, registerPassword);
  }

  function resetRegisterStep() {
    setClientRegisterError("");
    setRegisterStep(REGISTER_STEP.FRIEND_ID);
    setVerifiedFriendlyId("");
    setVerifiedDisplayName("");
    setVerifiedLevel(0);
    setRegisterPassword("");
    setConfirmPassword("");
  }

  return (
    <main className="auth-shell">
      <section className="auth-card">
        {showEnvironment ? (
          <div className="auth-environment-row">
            <span className="environment-badge">{activeEnvironment.label}</span>
          </div>
        ) : null}

        <div className="auth-mode-switch">
          <button
            type="button"
            className={mode === MODE.LOGIN ? "btn-game-primary is-selected" : "btn-game-secondary"}
            onClick={() => switchMode(MODE.LOGIN)}
          >
            Sign in
          </button>
          <button
            type="button"
            className={mode === MODE.REGISTER ? "btn-game-primary is-selected" : "btn-game-secondary"}
            onClick={() => switchMode(MODE.REGISTER)}
          >
            Create account
          </button>
        </div>

        {mode === MODE.LOGIN ? (
          <>
            <h1>Level Builder Login</h1>
            <p>Sign in to access the website builder.</p>

            <label>
              Username
              <input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
              />
            </label>

            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !busy) {
                    submitLogin();
                  }
                }}
              />
            </label>

            {activeError ? <p className="form-error">{activeError}</p> : null}

            <button
              type="button"
              className="btn-game-primary"
              disabled={busy}
              onClick={submitLogin}
            >
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </>
        ) : (
          <>
            <h1>Create Builder Account</h1>
            <p className="auth-helper">
              Use your in-game Friend ID. If the account exists and is above level 30, a builder
              account will be created with that Friend ID as the username and a 300-level limit.
            </p>

            {registerStep === REGISTER_STEP.FRIEND_ID ? (
              <>
                <label>
                  Friend ID
                  <input
                    value={friendlyId}
                    onChange={(event) => setFriendlyId(event.target.value)}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="username"
                    placeholder="AB12CD3"
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !registerCheckBusy) {
                        submitRegisterCheck();
                      }
                    }}
                  />
                </label>

                {activeError ? <p className="form-error">{activeError}</p> : null}

                <button
                  type="button"
                  className="btn-game-primary"
                  disabled={registerCheckBusy}
                  onClick={submitRegisterCheck}
                >
                  {registerCheckBusy ? "Checking account..." : "Continue"}
                </button>
              </>
            ) : (
              <>
                <label>
                  Friend ID
                  <input
                    value={verifiedFriendlyId}
                    readOnly
                    className="auth-readonly-input"
                    autoComplete="username"
                  />
                </label>

                <p className="auth-helper">
                  Verified account: <strong>{verifiedDisplayName}</strong> (level {verifiedLevel})
                </p>

                <label>
                  Password
                  <input
                    type="password"
                    value={registerPassword}
                    onChange={(event) => setRegisterPassword(event.target.value)}
                    autoComplete="new-password"
                    placeholder="At least 6 characters"
                  />
                </label>

                <label>
                  Confirm Password
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    autoComplete="new-password"
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !registerBusy) {
                        submitRegister();
                      }
                    }}
                  />
                </label>

                {activeError ? <p className="form-error">{activeError}</p> : null}

                <div className="auth-register-actions">
                  <button
                    type="button"
                    className="btn-game-secondary"
                    disabled={registerBusy}
                    onClick={resetRegisterStep}
                  >
                    Change Friend ID
                  </button>
                  <button
                    type="button"
                    className="btn-game-primary"
                    disabled={registerBusy}
                    onClick={submitRegister}
                  >
                    {registerBusy ? "Creating account..." : "Create account"}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </section>
    </main>
  );
}
